import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Lightbulb, Users, Target, Heart, Shield, ArrowLeft } from 'lucide-react';

const OurValues = () => {
  const values = [
    {
      icon: <Lightbulb className="h-16 w-16" />,
      title: "Innovation",
      subtitle: "Creating Smart Solutions",
      description: "We believe in pushing boundaries and exploring new possibilities. Innovation isn't just about technology – it's about finding creative solutions to real-world challenges that make a meaningful difference in people's lives.",
      details: [
        "Embracing emerging technologies and methodologies",
        "Encouraging creative thinking and experimentation",
        "Focusing on user-centered design and development",
        "Continuously learning and adapting to change"
      ],
      color: "from-blue-500 to-purple-600"
    },
    {
      icon: <Shield className="h-16 w-16" />,
      title: "Integrity",
      subtitle: "Building Trust Through Honesty",
      description: "Integrity forms the foundation of everything we do. We build trust through honest communication, transparent practices, and ethical decision-making that puts our clients' and community's best interests first.",
      details: [
        "Maintaining transparency in all business dealings",
        "Upholding ethical standards and practices",
        "Delivering on our commitments and promises",
        "Taking responsibility for our actions and decisions"
      ],
      color: "from-green-500 to-teal-600"
    },
    {
      icon: <Users className="h-16 w-16" />,
      title: "Collaboration",
      subtitle: "Working Together for Progress",
      description: "We believe that the best solutions emerge from collaborative efforts. By working together with our team, clients, and partners, we create synergies that drive meaningful progress and shared success.",
      details: [
        "Fostering open communication and dialogue",
        "Valuing diverse perspectives and experiences",
        "Building strong partnerships and relationships",
        "Creating inclusive environments where everyone can contribute"
      ],
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <Target className="h-16 w-16" />,
      title: "Impact",
      subtitle: "Turning Ideas into Solutions",
      description: "Every project we undertake is measured by its potential for positive impact. We're not just building software – we're creating solutions that solve real problems and improve people's lives and work experiences.",
      details: [
        "Focusing on solutions that create measurable value",
        "Prioritizing user experience and accessibility",
        "Measuring success by client and user satisfaction",
        "Continuously improving and refining our offerings"
      ],
      color: "from-yellow-500 to-orange-500"
    },
    {
      icon: <Heart className="h-16 w-16" />,
      title: "Compassion",
      subtitle: "Giving Back to Communities",
      description: "Compassion drives our commitment to social responsibility. Through our humanitarian initiatives and community support, we ensure that our success contributes to the wellbeing of those who need it most.",
      details: [
        "Dedicating 2.5% of revenue to humanitarian causes",
        "Supporting disadvantaged communities locally and globally",
        "Promoting social responsibility in business practices",
        "Creating positive impact beyond our immediate business"
      ],
      color: "from-pink-500 to-rose-600"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/about">
          <Button variant="outline" className="mb-8 text-vincotek-blue border-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to About Us
          </Button>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Our Core{' '}
              <span className="bg-gradient-to-r from-vincotek-yellow to-vincotek-blue bg-clip-text text-transparent">
                Values
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              These five core values guide every decision we make, shape our company culture, 
              and define our approach to business, technology, and life. They are not just words on a page – 
              they are the principles we live by every day.
            </p>
            
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 border border-gray-100">
              <blockquote className="text-2xl font-medium text-vincotek-dark italic mb-4">
                "Values are not just what we believe in – they are what we act on."
              </blockquote>
              <p className="text-gray-600">
                At Vincotek, our values are the foundation of our culture and the compass for our journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {values.map((value, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} flex flex-col lg:flex-row items-center gap-12 lg:gap-16`}>
                {/* Icon and Title */}
                <div className="lg:w-1/2 text-center lg:text-left">
                  <Card className="group hover:shadow-2xl transition-all duration-500 hover:-translate-y-4 bg-white border-0 overflow-hidden">
                    <CardContent className="p-12">
                      <div className="text-vincotek-blue mb-8 flex justify-center lg:justify-start group-hover:scale-110 transition-transform duration-300">
                        {value.icon}
                      </div>
                      <h3 className="text-3xl font-bold text-vincotek-dark mb-3">
                        {value.title}
                      </h3>
                      <p className="text-lg font-semibold text-vincotek-blue mb-6">
                        {value.subtitle}
                      </p>
                      <p className="text-gray-600 leading-relaxed mb-8">
                        {value.description}
                      </p>
                      
                      {/* Details List */}
                      <div className="space-y-3">
                        {value.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-sm text-gray-600 leading-relaxed">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Visual Representation */}
                <div className="lg:w-1/2">
                  <div className="relative">
                    <div className="w-full h-80 bg-gradient-to-br from-vincotek-yellow/20 to-vincotek-blue/20 rounded-2xl flex items-center justify-center">
                      <div className="w-48 h-48 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center shadow-2xl">
                        <div className="text-white">
                          {React.cloneElement(value.icon, { className: "h-20 w-20" })}
                        </div>
                      </div>
                    </div>
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-vincotek-yellow rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-vincotek-dark font-bold text-lg">{index + 1}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values in Action */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
                Values in Action
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our values aren't just aspirational – they guide our daily operations and long-term strategy.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="bg-gradient-to-br from-gray-50 to-blue-50/30 border-0 p-8">
                <CardContent className="p-0">
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    In Our Products
                  </h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 mr-3"></div>
                      User-centered design that prioritizes accessibility
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 mr-3"></div>
                      Innovative solutions that solve real-world problems
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 mr-3"></div>
                      Transparent communication about features and limitations
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-gray-50 to-yellow-50/30 border-0 p-8">
                <CardContent className="p-0">
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    In Our Culture
                  </h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3"></div>
                      Inclusive environment that values diverse perspectives
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3"></div>
                      Open collaboration and knowledge sharing
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3"></div>
                      Commitment to continuous learning and growth
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Share Our Values?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            If these values resonate with you, we'd love to connect. Whether you're interested in 
            our services, partnership opportunities, or joining our team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Get in Touch
              </Button>
            </Link>
            <Link to="/careers">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-vincotek-dark px-8 py-3 rounded-full transition-all duration-300">
                Explore Careers
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurValues;